import React, { forwardRef, useCallback, useState } from 'react'
import {
  Button,
  ButtonProps,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  cn,
} from 'ui'
import { useSigning } from '../../../Context'
import { useSigningForm } from '../../Provider'
import { BindingField } from '../BindingField'
import { SigningFormFieldProps } from '../Switcher'
import { useMeasure } from '@uidotdev/usehooks'
import { SignatureComposition } from './Composition'
import { SignatureDimensionsProvider } from './SignatureDimensionsContext'
import { useSignatureState } from '../../../../../hooks/useSignatureState'
import { SignatureProvider } from './Context'
import { IconEraser } from '@tabler/icons-react'
import { useDeviceDetect } from '@autosign/core/hooks/useDeviceDetect'
import { usernameSchema } from 'bff/schemas/username.schema'

export interface SignatureProps {
  onChange?: OnChangeSignatureFN
  error?: boolean
  className?: string
  width?: number
  height?: number
}

export const SIGNATURE_PLACEHOLDER_HEIGHT = 60

export interface SignatureHandler {
  getImage: () => string
}

export const SignaturePlaceholder = forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement> & {
    withCloseIcon?: boolean
  }
>(({ children, withCloseIcon = true, ...props }, ref) => {
  return (
    <div className='w-full p-5' {...props}>
      <div className='flex flex-row border-b border-b-[#555d65] items-center space-x-2 pb-2'>
        {children}
      </div>
    </div>
  )
})

export const SignatureToolbar = ({
  children,
}: {
  children: React.ReactNode
}) => {
  return (
    <div className='absolute z-[10000] top-0 right-0'>
      <div className='flex flex-row items-center space-x-2 p-2'>{children}</div>
    </div>
  )
}

export const SignatureToolbarButton = forwardRef<
  React.ElementRef<typeof Button>,
  ButtonProps
>((props, ref) => {
  return (
    <Button
      className='text-gray-500'
      size='xs'
      variant={'ghost'}
      ref={ref}
      {...props}
    />
  )
})

export const SignatureClearButton = forwardRef<
  React.ElementRef<typeof Button>,
  ButtonProps
>((props, ref) => {
  return (
    <SignatureToolbarButton {...props}>
      <IconEraser />
    </SignatureToolbarButton>
  )
})

export const SignatureCard = forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement> & {
    isInvalid?: boolean
  }
>(({ isInvalid, className, ...props }, ref) => {
  return (
    <div
      className={cn(
        'pointer rounded-md h-[12rem] flex items-end justify-start relative bg-gray-100',
        {
          'border border-red-500': isInvalid,
          'border border-[#e9ecef]': !isInvalid,
        },
        className
      )}
      ref={ref}
      {...props}
    />
  )
})

type OnChangeSignatureFN = () => void

export type SignatureValue =
  | {
      type: 'draw'
      value: string
    }
  | {
      type: 'type'
    }

export const DisplaySignature = ({ signature }: { signature: string }) => {
  return (
    <div className='2'>
      <img src={signature} />
    </div>
  )
}

const SignedBy = ({
  signatureSrc,
  onRemove,
}: {
  signatureSrc: string
  onRemove: () => void
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <img className='w-full h-full object-contain' src={signatureSrc} />
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem className='text-red-500' onClick={onRemove}>
          Borrar
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const getHeigth = () => {
  return Math.round(
    Math.max(
      document.documentElement.clientHeight || 0,
      window.innerHeight || 0
    ) * 0.55
  ) // 55% of the screen
}

export const SignatureField = (props: SigningFormFieldProps) => {
  const device = useDeviceDetect()

  const [isOpen, setIsOpen] = useState(false)
  const [signatureSessionId, setSignatureSessionId] = useState<string>('')

  const form = useSigningForm()

  const signing = useSigning()

  const signature = form.watch(props.path)

  const [ref, { width, height }] = useMeasure()

  const { path } = props

  const { isLoading, commitSignature } = useSignatureState({
    onClose: () => setIsOpen(false),
    onPersist: props.onPersist,
    onSignaturePersisted: (signature) => {
      form.setValue(path, signature)
    },
  })

  const handleClickSign = useCallback(() => {
    if (signing.userSignature && !signing.hsm_enabled) {
      form.setValue(props.path, signing.userSignature)
      commitSignature(signing.userSignature)
    } else {
      const values = form.getValues()

      const prevSignature = signing.template.fields.find(
        (f) => f.type === 'signature' && !!values.fields[f.id]
      )

      if (prevSignature) {
        const prevSignatureValue = values.fields[prevSignature.id]!

        commitSignature(prevSignatureValue)
      }

      if (!prevSignature) {
        setIsOpen(true)
      }
    }
  }, [commitSignature, signing.userSignature, signing.hsm_enabled])

  if (signature) {
    return (
      <SignedBy
        signatureSrc={signature}
        onRemove={() => {
          signing.remote
            .expireSignatureSession(props.field.id)
            .catch(console.error)

          form.setValue(props.path, '')
        }}
      />
    )
  }

  const heightValue = device === 'desktop' ? 192 : getHeigth()

  return (
    <SignatureDimensionsProvider width={width!} height={heightValue}>
      <Dialog onOpenChange={setIsOpen} open={isOpen}>
        <BindingField onClick={handleClickSign} id={props.field.id}>
          Firmar
        </BindingField>
        <DialogContent size={'xl'} withCloseButton={false}>
          <div
            ref={ref}
            className={cn({
              'h-[85svh]': device !== 'desktop',
            })}
          >
            <SignatureProvider
              createSession={async () => {
                const signatureSession =
                  await signing.remote.createSignatureSession(props.field.id)
                setSignatureSessionId(signatureSession.id)
                return signatureSession
              }}
              expireSignatureSession={() =>
                signing.remote.expireSignatureSession(props.field.id)
              }
              onSignatureReceived={(signature) => {
                form.setValue(path, signature)
                setIsOpen(false)
              }}
              onSignaturePersisted={(signature) =>
                form.setValue(props.path, signature)
              }
              retrieveSignatureSession={() => {
                return signing.remote.retrieveSignatureSession(
                  props.field.id,
                  signatureSessionId
                )
              }}
            >
              <SignatureComposition
                onClose={() => setIsOpen(false)}
                commitSignature={commitSignature}
                isLoading={isLoading}
                signer={{
                  name: signing.recipient.name,
                  email: signing.recipient.email,
                }}
              />
            </SignatureProvider>
          </div>
        </DialogContent>
      </Dialog>
    </SignatureDimensionsProvider>
  )
}
